"use client";

import { useEffect, useState, type ReactNode } from "react";
import type { NavSpec, CurrentUser } from "./types";
import { Sidebar, type SidebarProps } from "./Sidebar";
import { Topbar } from "./Topbar";
import { useInjectStyles } from "../utils/useInjectStyles";
import { readJSON, writeJSON } from "../utils/storage";

export interface AppShellProps {
  nav: NavSpec;
  activeHref?: string;
  brand?: ReactNode;
  title?: ReactNode;
  user?: CurrentUser;
  topbarActions?: ReactNode;
  children: ReactNode;
  linkComponent?: SidebarProps["linkComponent"];
  defaultCollapsed?: boolean;
}

/**
 * 桌機側欄開合偏好的 localStorage key（僅記桌機；行動預設不受它影響，見下方
 * `isMobileViewport`／規範 §6.4）。
 */
const SIDEBAR_COLLAPSED_STORAGE_KEY = "ccheng_admin_sidebar_collapsed";

/**
 * 行動寬度斷點：<768px（沿用 Tailwind 預設的 `md`，本套件雖不吃 Tailwind pipeline
 * ，但沿用同一個數字，避免消費端與這裡各自認定的「手機」寬度對不起來）。
 */
const MOBILE_BREAKPOINT_PX = 768;

function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT_PX;
}

/** 側欄選單＋頂欄的後台/前台幹部頁通用版型（裁決 5-4：web/admin 都用得到）。 */
export function AppShell({
  nav,
  activeHref,
  brand,
  title,
  user,
  topbarActions,
  children,
  linkComponent,
  defaultCollapsed = false,
}: AppShellProps) {
  useInjectStyles();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  /*
   * B2 瀏覽器稽核（高）：390 寬所有頁面載入時橫向捲動 —— 側欄在行動寬度預設展開
   * （260px），把 flex row 撐爆到 390 以外。首次繪製仍用 defaultCollapsed（SSR
   * 沒有 window，無法提前判斷寬度；比照 Sidebar.tsx 讀釘選清單的作法，掛載後
   * 再用 useEffect 校正一次，非常短暫，不影響驗收看到的 DOM/文字）：
   *   - 行動寬度：一律預設收合，不管桌機記憶的偏好是什麼。
   *   - 桌機寬度：讀 localStorage 記住的開合偏好；沒存過就維持 defaultCollapsed。
   */
  useEffect(() => {
    if (isMobileViewport()) {
      setCollapsed(true);
      return;
    }
    const saved = readJSON<boolean>(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (typeof saved === "boolean") setCollapsed(saved);
  }, []);

  function toggleSidebar() {
    setCollapsed((c) => {
      const next = !c;
      // 只記桌機的開合偏好：行動寬度切換不寫入，才不會反過來污染桌機下次的預設，
      // 也不會讓下次行動寬度載入誤讀到「桌機曾經展開過」。
      if (!isMobileViewport()) writeJSON(SIDEBAR_COLLAPSED_STORAGE_KEY, next);
      return next;
    });
  }

  return (
    <div className="cui-reset cui-shell">
      <Sidebar nav={nav} activeHref={activeHref} collapsed={collapsed} brand={brand} linkComponent={linkComponent} />
      {/*
       * 行動寬度且展開時，Sidebar 本身在 CSS 裡切成 position:fixed 覆蓋層
       * （不再推擠 .cui-shell-main 的寬度），這片半透明背板才會顯示、點擊可收合
       * ——純裝飾／滑鼠捷徑，鍵盤使用者仍可用一定會渲染的 ☰ 鈕收合，見 Topbar。
       */}
      <div className="cui-shell-backdrop" aria-hidden="true" onClick={() => setCollapsed(true)} />
      <div className="cui-shell-main">
        <Topbar
          title={title}
          onToggleSidebar={toggleSidebar}
          collapsed={collapsed}
          user={user}
          actions={topbarActions}
        />
        <main className="cui-shell-content">{children}</main>
      </div>
    </div>
  );
}
